import { AppSendPush } from "./AppSendPush";
import { kTimeTextCore } from "../../../Admin/kReusableCoreTS/kTimeTextCore";
import { AppMsgTickPaint, IMaNode } from "./AppMsgActions";
import { AppMarkRead } from "./AppMarkRead";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppChatAppend, AppChatItem, IChatDoc, IChatFrame, IChatNode } from "./AppChatAppend";
import { AppChatRules } from "./AppChatRules";
import { AppLinkNodes, ILinkDoc, ILinkNode } from "./AppLinks";
import { AppLiveEdit, AppLiveEditDone, IEditDoc } from "./AppLiveEdit";
import { AppLiveChange, AppLiveEvents, AppLivePoll, AppLivePollPaint, AppLiveReaction, IPollDoc } from "./AppLiveEvents";
import { AppLiveReact, ILiveReactDoc, ILiveReactor } from "./AppLiveReact";
import { AppLiveAnswer, AppLiveCursor, AppLiveItem, AppLivePatch, AppLiveRules } from "./AppLiveRules";
import { AppSignUpApi } from "./AppSignUpApi";
import { AppTranslateBubble, ITrDoc, ITrNode } from "./AppTranslateBubble";

// AppLiveFeed - NEW MESSAGES APPEAR IN THE OPEN CHAT WITHIN SECONDS (translation step 1). While a contact page is
// open AND the tab is visible, one long poll to `/live` at a time (`AppLiveRules`' header says why a long poll);
// what comes back is drawn into the `blob:` frame through `AppChatAppend.PlaceLive` - the same day placement as
// a sent message, the page's own bubble classes, `textContent` only.
//
// ⛔ ONE MESSAGE, ONE BUBBLE: a message the page already shows (`data-mid`) is never drawn; one the owner sent from
//    this app is ADOPTED by its `data-wac-id` bubble (`AppChatAppend.AdoptSent`); the cursor (`AppLiveCursor`)
//    never asks for an id twice.
// ⭐ THE SCROLL RULE: a reader at the bottom of the conversation follows it; a reader scrolled up keeps their place
//    and gets a "New message ↓" pill (a button, in the frame's linked sheet - never an inline style).
// ⛔ STOPS when the tab is hidden (`Visible(false)` aborts the request), when the frame is replaced or removed
//    (`Stop`, `isConnected`), and FOR THE VISIT when the service has no `/live` (`Off` - no retry storm).
// ⛔ READ-ONLY (decision 9): nothing here can reach a sender - `AppLive.test.ts` greps for it.
// ⚠ SHAPED BY INTERFACES so the suite drives it with a fake document, transport and clock.
// ⚠ ITS OWN XHR (`AppLivePost`), like `AppAccountPost`/`AppExport`: the shared REST transport is SharePoint-bound
//    (`AppSource.ts`'s header), and a long poll must be ABORTABLE when the tab hides or the page changes.
// ⭐ TYPING (2026-09-30, `Bridge/src/TypingState.ts`): each ask says what the page shows (`typing`), the service answers
//    EARLY when the contact starts or stops typing, and a "typing…" / "recording audio…" line sits at the bottom of the
//    conversation (the FIRST child of the reversed `.chat`, `div.wac-typing`, styled by `AppLiveRules.Css`, empty =
//    hidden). It goes when the service says so, when their message is drawn, when the tab hides, or - if nobody renews
//    it - after `AppLiveRules.TypingSafetyMs`. ⛔ `textContent` only.

/** The host's ear for an answer's `sends` (`AppSendPush`). */
export type AppSendsHeard = (Sends: Record<string, Record<string, unknown>>) => void;

/** The transport: one POST, answered once with (status, text); `Abort` answers nothing. */
export interface IAppLivePost {
    Post(Url: string, Body: string, Done: (Status: number, Text: string) => void): void;
    Abort(): void;
}

/** Timers, so the suite can run them by hand. */
export interface IAppLiveClock {
    Later(Ms: number, Fn: () => void): number;
    Cancel(Id: number): void;
}

/** What the feed needs of the frame element: its `load`, and whether it is still in the app's page. */
export interface IAppLiveFrame extends IChatFrame {
    isConnected?: boolean;
}

/** The scroll pane (`.chat`, `column-reverse`) and the pill - the few DOM members the feed touches. */
export interface IAppLivePane extends IChatNode {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
    addEventListener(Type: string, Listener: () => void): void;
}

export interface IAppLivePill extends IChatNode {
    removeAttribute(Name: string): void;
    addEventListener(Type: string, Listener: () => void): void;
}

/** Who is asking - read when the feed is made (a new page makes a new feed). */
export class AppLiveWho {
    public ServiceUrl: string = "";
    public Phone: string = "";
    public Key: string = "";
    public Page: string = "";
    /** ⭐ "" = the open page (by `Page`); `AppLiveRules.ChatMode` = a chat with no page yet, by `Chat` (2026-09-28). */
    public Mode: string = "";
    public Chat: string = "";
}

export class AppLiveFeed {
    /** ⛔ The service answered without the route (dormant / older / 404): no feed again until the app reloads. */
    public static Off: boolean = false;
    /** After an answer, the next ask goes after this pause (a burst of messages is not a tight loop). */
    public static readonly AgainMs: number = 300;

    private Appender: AppChatAppend;
    private Who: AppLiveWho;
    private Transport: IAppLivePost;
    private Frame: IAppLiveFrame | null = null;
    private Cursor: AppLiveCursor = new AppLiveCursor();
    private Begun: boolean = false;
    private Stopped: boolean = false;
    private Hidden: boolean = false;
    private InFlight: boolean = false;
    private Latch: AppLiveLatch;
    private Failures: number = 0;
    /** Answers in a row that brought only ids the cursor already holds (`PauseAfter`). */
    private Repeats: number = 0;
    private Pill: IAppLivePill | null = null;
    /** How many requests went out - for the suite and the e2e harness. */
    public Asked: number = 0;
    /** ⭐ Ids whose translation is on its way -> since when (ms); asked for as `wait`, dropped after `WaitMaxMs`. */
    private Waiting: Record<string, number> = {};
    /** When the previous answer landed (ms), 0 = none yet - the next ask's `gap` (the service's latency book). */
    private AnsweredMs: number = 0;
    /** The clock for `gap` and the waits - a field so the suite can set it. */
    public Now: () => number = Date.now;
    /** ⭐ Their message ids this page already asked to mark read (`AppMarkRead`) - each once. */
    private ReadAsked: Record<string, boolean> = {};
    /** ⭐ What the typing line says now (`composing` | `recording` | "") - sent with every ask. */
    private TypingShown: string = "";
    private TypingTimer: number = 0;
    /**
     * ⭐ LIVE REACTIONS, EDITS AND REVOKES (2026-10-01, `AppLiveEvents`): an answer's `reactions` are folded onto their
     * bubbles' pills (`AppLiveReact`), its `changes` put on their bubbles (`AppLiveEdit`). Both are events with their
     * own ids, taken by the cursor like messages. ⛔ Neither can send.
     */
    public React: AppLiveReact;
    private Edits: AppLiveEdit = new AppLiveEdit();
    /** ⭐ (2026-10-20) Live poll tallies (`AppLivePollPaint`). */
    private Tallies: AppLivePollPaint = new AppLivePollPaint();

    public constructor(Appender: AppChatAppend, Who: AppLiveWho, Transport: IAppLivePost, Clock: IAppLiveClock) {
        this.Appender = Appender;
        this.Who = Who;
        this.Transport = Transport;
        this.Latch = new AppLiveLatch(Clock, this.Ask);
        this.React = new AppLiveReact(null, this.ReactPage);
    }

    /**
     * The frame's reactor (`AppReactions`), when the page can react: a live echo then confirms the owner's own
     * optimistic reaction instead of racing it. Without one, live reactions are simply drawn.
     */
    public UseReactor = (Reactor: ILiveReactor | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.React = new AppLiveReact(Reactor, this.ReactPage);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.UseReactor");
        }
    };

    /** The page as the reaction drawer needs it, or null (not loaded, or the frame was replaced). */
    private ReactPage = (): ILiveReactDoc | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.Stopped === true) { return null; }
            return this.Appender.Page() as unknown as ILiveReactDoc | null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.ReactPage");
            return null;
        }
    };

    /** An answer's edits and revokes onto their bubbles; a translation still on its way is waited for. */
    private Change = (Changes: AppLiveChange[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc !== null && Changes.length > 0) {
                const Done: AppLiveEditDone = this.Edits.Apply(Doc as unknown as IEditDoc, Changes);
                for (const Id of Done.Settled) { delete this.Waiting[Id]; }
                for (const Id of Done.Pending) { this.Waiting[Id] = this.Now(); }
            }
            this.Cursor.Took(Changes);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Change");
        }
    };

    /** ⭐ (2026-10-20) An answer's poll tallies onto their bubbles (`AppLivePollPaint`); the cursor takes the vote ids. */
    private PollTo = (Polls: AppLivePoll[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc !== null && Polls.length > 0) { this.Tallies.Apply(Doc as unknown as IPollDoc, Polls); }
            this.Cursor.Took(Polls);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.PollTo");
        }
    };

    /** An answer's reaction rows onto their bubbles' pills. */
    private ReactTo = (Events: AppLiveReaction[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: ILiveReactDoc | null = this.ReactPage();
            if (Doc !== null && Events.length > 0) { this.React.Apply(Doc as ILiveReactDoc, Events); }
            this.Cursor.Took(Events);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.ReactTo");
        }
    };

    /** ⚠ Attach AFTER the appender: its `load` listener runs first, so the page is there when this one runs. */
    public Attach = (Frame: IAppLiveFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Attach");
        }
    };

    /** The page is in: the cursor starts at its newest minute, and the first ask goes. */
    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Begun === true || this.Stopped === true || AppLiveFeed.Off === true) { return; }
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc === null) { return; }
            this.Begun = true;
            const Newest: string = AppLiveFeed.NewestMinute(Doc as IChatDoc);
            this.Cursor.Begin(AppLiveRules.FloorOf(Newest), AppLiveFeed.MidsIn(Doc as IChatDoc, Newest));
            this.Latch.Ask();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.OnLoad");
        }
    };

    /** The tab was shown or hidden. Hidden: the request is dropped and nothing is asked until it is shown. */
    public Visible = (Shown: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            this.Hidden = Shown === false;
            if (this.Hidden === true) { this.Quiet(); this.ShowTyping(""); return; }
            // ⭐ SHOWN AGAIN: ask AT ONCE (a backoff wait pending from before is cut short) - caught up within ~1 s.
            if (this.Begun === true && this.InFlight === false) {
                if (this.Latch.Timer !== 0) { this.Latch.Clock.Cancel(this.Latch.Timer); this.Latch.Timer = 0; }
                this.Latch.Ask();
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Visible");
        }
    };

    /** The frame is being replaced (another contact, a reload of this one): no more asking, ever. */
    public Stop = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a stop that a latched gate skipped would leave a poll
        // running against a page that is gone.
        try {
            this.Stopped = true;
            this.Quiet();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Stop");
        }
    };

    /** The timer cancelled and the request in flight dropped. */
    private Quiet = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - see Stop.
        try {
            const Clock = this.Latch.Clock;
            const Poll: number = this.Latch.Timer;
            if (Poll !== 0) { Clock.Cancel(Poll); this.Latch.Timer = 0; }
            const Airborne = this.Transport;
            const Abandon: boolean = this.InFlight === true;
            if (Abandon === true) { this.InFlight = false; Airborne.Abort(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Quiet");
        }
    };

    /** One request, if none is out and the page is open and shown. */
    private Ask = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Latch.Timer = 0;
            if (this.Stopped === true || this.Hidden === true || this.InFlight === true || AppLiveFeed.Off === true) { return; }
            if (this.Frame !== null && (this.Frame as IAppLiveFrame).isConnected === false) { this.Stop(); return; }
            const Url: string = AppSignUpApi.UrlFor(this.Who.ServiceUrl, AppLiveRules.Route);
            if (Url.length === 0) { this.Stop(); return; }
            this.InFlight = true;
            this.Asked = this.Asked + 1;
            this.Expire();
            let Gap: number = -1;
            if (this.AnsweredMs > 0) { Gap = Math.max(0, this.Now() - this.AnsweredMs); }
            // ⭐ TICKS (2026-10-14): what each own bubble shows goes with the ask (`AppMsgTickPaint`).
            // ⭐ READ RECEIPTS (2026-10-14): THEIR newest ids, once each, only from a visible tab with the switch on.
            let Watched: Record<string, string> = {};
            if (this.OnSends !== null) { Watched = AppSendPush.Shared.Watched(); }
            this.Transport.Post(Url, AppSendPush.WithSending(AppMarkRead.WithRead(AppMsgTickPaint.WithTicks(AppLiveEvents.WithPolls(AppLiveRules.BodyOf(this.Who.Phone, this.Who.Key, this.Who.Page,
                this.Cursor.Floor(), this.Cursor.Seen(), this.Who.Mode, this.Who.Chat, Object.keys(this.Waiting), Gap,
                this.TypingShown, null, null, true)), this.Appender.Page() as unknown as IMaNode | null),
                this.Appender.Page() as unknown as IMaNode | null, this.ReadAsked), Watched), this.OnAnswer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Ask");
        }
    };

    /** An answer: draw what is new and ask again; or wait; or stop - `AppLiveRules.Parse` decides which. */
    public OnAnswer = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.InFlight === false || this.Stopped === true) { return; }
            this.InFlight = false;
            const Got: AppLiveAnswer = AppLiveRules.Parse(Status, Text);
            if (Got.Off === true) { AppLiveFeed.Off = true; this.Stop(); return; }
            if (Got.Stop === true) { this.Stop(); return; }
            if (Got.Ok === true) {
                this.Failures = 0;
                this.AnsweredMs = this.Now();
                // ⛔ ASKED BEFORE ANYTHING IS TAKEN (the cursor then holds every id of this answer).
                const Polls: AppLivePoll[] = AppLiveEvents.PollsIn(Text);
                const Pause: number = this.PauseAfter(Got, Polls);
                this.Draw(Got.Items);
                // ⭐ AFTER the bubbles (a just-sent bubble has its WhatsApp id by now - `AdoptSent`), BEFORE the patches
                // (a patch for an edited message is the translation of its NEW words).
                this.Change(Got.Changes);
                this.ReactTo(Got.Reactions);
                this.PollTo(Polls);
                this.Patch(Got.Patches);
                // ⭐ AFTER the bubbles: their message ended the old line, and the answer's state is the newest word.
                if (Got.TypingKnown === true) { this.ShowTyping(Got.Typing); }
                AppMsgTickPaint.FromAnswer(Text, this.Appender.Page() as unknown as IMaNode | null);
                this.HearSends(Text);
                this.Again(Pause);
                return;
            }
            if (Got.RetrySec > 0) { this.Again(Got.RetrySec * 1000); return; }
            this.Failures = this.Failures + 1;
            this.Again(AppLiveRules.BackoffMs(this.Failures));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.OnAnswer");
        }
    };

    /**
     * How long until the next ask. `AgainMs` - unless the answer brought ONLY what the cursor already holds.
     * ⛔ THE LOOP THIS STOPS (verifier, 2026-10-01): `seen` carries at most `MaxSeen` (40) ids, all of them at the
     * floor's own instant. Since reactions and edits ride the cursor too, more than 40 rows in ONE second is no longer
     * unthinkable - and the 41st was then answered again on every ask, 300 ms apart, for as long as the tab stayed
     * open (three requests a second against the service, per tab). An answer with nothing new now waits like a lost
     * connection does (1, 2, 4, 8 s); the first answer that brings something new ends it.
     */
    private PauseAfter = (Got: AppLiveAnswer, Polls: AppLivePoll[] = []): number => {
        if (getCoreUtils().KErrorMode === true) { return AppLiveFeed.AgainMs; }
        try {
            let Ids: number = 0;
            let Fresh: number = 0;
            const All: { Id: string }[] = ([] as { Id: string }[]).concat(Got.Items, Got.Changes, Got.Reactions, Polls);
            for (const One of All) {
                Ids = Ids + 1;
                if (this.Cursor.Has(One.Id) === false) { Fresh = Fresh + 1; }
            }
            if (Ids === 0 || Fresh > 0) { this.Repeats = 0; return AppLiveFeed.AgainMs; }
            this.Repeats = this.Repeats + 1;
            return AppLiveRules.BackoffMs(this.Repeats);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.PauseAfter");
            return AppLiveFeed.AgainMs;
        }
    };

    /** ⭐ The answer's `sends` to the host - each settles a bubble once (`AppSendPush.Settle` in the host). */
    private HearSends = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.OnSends === null) { return; }
            const Sends: Record<string, Record<string, unknown>> = AppSendPush.SendsIn(Text);
            if (Object.keys(Sends).length === 0) { return; }
            (this.OnSends as AppSendsHeard)(Sends);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.HearSends");
        }
    };

    private Again = (Ms: number): void => {
        AppLiveKick.Again(this.Stopped, this.Hidden, Ms, this.Latch, "AppLiveFeed.Again");
    };

    /** Draw each new message under its day; follow the conversation, or show the pill. */
    private Draw = (Items: AppLiveItem[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc === null || Items.length === 0) { this.Cursor.Took(Items); return; }
            const Pane: IAppLivePane | null = (Doc as IChatDoc).querySelector(".chat") as IAppLivePane | null;
            let Follow: boolean = true;
            if (Pane !== null) { Follow = AppLiveRules.AtBottom(Pane.scrollTop, Pane.scrollHeight, Pane.clientHeight); }
            let Placed: number = 0;
            for (const Item of Items) {
                if (this.Cursor.Has(Item.Id) === true) { continue; }
                if (Item.FromMe === true && this.Appender.AdoptSent(Item.Id, AppLiveRules.Shown(Item)) === true) { continue; }
                const Bubble: IChatNode | null = this.Appender.PlaceLive(AppLiveFeed.ChatItemOf(Item));
                if (Bubble === null) { continue; }
                Placed = Placed + 1;
                if (Item.FromMe === false) { this.ShowTyping(""); }
                // ⭐ ITS TRANSLATION IS ON ITS WAY: the bubble went as written; the patch swaps the words when it lands.
                if (Item.Pending === true) { (Bubble as IChatNode).setAttribute(AppLiveRules.WaitAttr, "1"); this.Waiting[Item.Id] = this.Now(); }
            }
            this.Cursor.Took(Items);
            if (Placed === 0 || Pane === null) { return; }
            this.SeatTyping(Doc as IChatDoc);
            if (Follow === true) { AppLiveFeed.ToBottom(Pane as IAppLivePane); return; }
            this.ShowPill(Doc as IChatDoc, Pane as IAppLivePane);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Draw");
        }
    };

    /**
     * ⭐ TRANSLATIONS THAT LANDED AFTER THEIR BUBBLE: the bubble (found by `data-mid`) gets my language and the flag
     * revealing what was written (`AppTranslateBubble.Mark`, the page's own shape); a "not needed" patch only stops the
     * wait. ⛔ Text by `textContent` only.
     */
    private Patch = (Patches: AppLivePatch[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: IChatDoc | null = this.Appender.Page();
            for (const One of Patches) {
                delete this.Waiting[One.Id];
                if (Doc === null) { continue; }
                const Bubble: IChatNode | null = AppLiveFeed.BubbleFor(Doc as IChatDoc, One.Id);
                if (Bubble === null) { continue; }
                (Bubble as IChatNode).setAttribute(AppLiveRules.WaitAttr, "0");
                const Words: IChatNode | null = (Bubble as IChatNode).querySelector(".txt");
                if (One.Needed === false || Words === null) { continue; }
                const Written: string = nz((Words as IChatNode).textContent);
                // The translation with its links, as the rebuilt page draws a translated bubble's shown text (`AppLinks`).
                AppLinkNodes.Fill(Doc as unknown as ILinkDoc, Words as unknown as ILinkNode, One.Text);
                AppTranslateBubble.Mark(Doc as unknown as ITrDoc, Bubble as unknown as ITrNode, Written, false);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Patch");
        }
    };

    /** A translation not back within `WaitMaxMs` is no longer asked for; its bubble keeps what was written. */
    private Expire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Now: number = this.Now();
            const Doc: IChatDoc | null = this.Appender.Page();
            for (const Id of Object.keys(this.Waiting)) {
                if (Now - this.Waiting[Id] < AppLiveRules.WaitMaxMs) { continue; }
                delete this.Waiting[Id];
                if (Doc === null) { continue; }
                const Bubble: IChatNode | null = AppLiveFeed.BubbleFor(Doc as IChatDoc, Id);
                if (Bubble !== null) { (Bubble as IChatNode).setAttribute(AppLiveRules.WaitAttr, "0"); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.Expire");
        }
    };

    /** The bubble with this `data-mid`. ⛔ Compared in a loop - the id never enters a selector. */
    private static BubbleFor = (Doc: IChatDoc, Id: string): IChatNode | null => {
        return AppLiveEdit.BubbleFor(Doc, Id);
    };

    /**
     * ⭐ THE TYPING LINE: say it (or clear it - an empty line is hidden by `AppLiveRules.Css`), remember it for the next
     * ask, and clear it after `TypingSafetyMs` unless an answer renews it.
     */
    public ShowTyping = (State: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Clean: string = AppLiveRules.TypingOf(State);
            this.TypingShown = Clean;
            if (this.TypingTimer !== 0) { this.Latch.Clock.Cancel(this.TypingTimer); this.TypingTimer = 0; }
            if (Clean.length > 0) { this.TypingTimer = this.Latch.Clock.Later(AppLiveRules.TypingSafetyMs, this.OnTypingStale); }
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc === null) { return; }
            const Line: IChatNode | null = this.TypingLine(Doc as IChatDoc, Clean.length > 0);
            if (Line !== null) { (Line as IChatNode).textContent = AppLiveRules.TypingText(Clean); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.ShowTyping");
        }
    };

    /** What the line says now - for the suite. DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public TypingNow = (): string => {
        return this.TypingShown;
    };

    /** Nobody renewed it: the line goes. */
    private OnTypingStale = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.TypingTimer = 0;
            this.ShowTyping("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.OnTypingStale");
        }
    };

    /** The page's typing line (made at the bottom when `Make` and there is none), or null. */
    private TypingLine = (Doc: IChatDoc, Make: boolean): IChatNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Chat: IChatNode | null = Doc.querySelector(".chat");
            if (Chat === null) { return null; }
            const Have: IChatNode | null = (Chat as IChatNode).querySelector("." + AppLiveRules.TypingClass);
            if (Have !== null || Make === false) { return Have; }
            const Made: IChatNode = Doc.createElement("div");
            Made.className = AppLiveRules.TypingClass;
            Made.setAttribute("role", "status");
            Made.setAttribute("aria-live", "polite");
            (Chat as IChatNode).insertBefore(Made, (Chat as IChatNode).firstElementChild);
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.TypingLine");
            return null;
        }
    };

    /** A new day may have gone in under it (`AppChatAppend.RowsFor` puts a day first): the line goes back to the bottom. */
    private SeatTyping = (Doc: IChatDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Chat: IChatNode | null = Doc.querySelector(".chat");
            if (Chat === null) { return; }
            const Line: IChatNode | null = (Chat as IChatNode).querySelector("." + AppLiveRules.TypingClass);
            if (Line === null || (Chat as IChatNode).firstElementChild === Line) { return; }
            (Chat as IChatNode).insertBefore(Line as IChatNode, (Chat as IChatNode).firstElementChild);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.SeatTyping");
        }
    };

    /**
     * ⭐ ASK NOW (2026-09-28): the network came back (`online`) or the owner is looking again - a pending wait (a
     * backoff after a lost connection) is cut short so the feed catches up at once instead of up to 8 s later.
     */
    /**
     * ⭐ SEND AT ONCE (`AppSendPush`): a send was just queued from this page - the held ask (which does not name it) is
     * dropped and a new one goes at once carrying `sending`, so the service can answer the moment the receipt lands.
     */
    public AskAgain = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Hidden === true || this.Begun === false) { return; }
            this.Quiet();
            this.Latch.Ask();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.AskAgain");
        }
    };

    /** ⭐ Who hears an answer's `sends` (`AppMain.OnSendsPushed`); null = nobody (the feed then sends no `sending`). */
    public OnSends: AppSendsHeard | null = null;

    public Kick = (): void => {
        AppLiveKick.Now(this.Stopped, this.Hidden, this.InFlight, this.Begun, this.Latch, "AppLiveFeed.Kick");
    };

    /** A live item as the appender's bubble - the page's words, the reader's clock, the UTC minute's day. */
    public static ChatItemOf = (Item: AppLiveItem): AppChatItem => {
        const Out: AppChatItem = new AppChatItem();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Mid = Item.Id;
            Out.Side = "them";
            if (Item.FromMe === true) { Out.Side = "me"; }
            Out.Text = AppLiveRules.Shown(Item);
            // ⭐ TRANSLATION: the original behind the bubble's flag (only when the item was translated).
            Out.Original = AppLiveRules.OriginalOf(Item);
            Out.Edited = Item.Edited === true;
            Out.Media = Item.Media;
            Out.Minute = AppChatRules.MinuteOfIso(Item.At);
            Out.Clock = kTimeTextCore.ClockOfIso(Item.At);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.ChatItemOf");
            return new AppChatItem();
        }
    };

    /** The bottom of a reversed pane (0 in Chrome/Firefox/Safari; `scrollHeight` clamps there in either model). */
    private static ToBottom = (Pane: IAppLivePane): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Pane.scrollTop = 0;
            if (AppLiveRules.AtBottom(Pane.scrollTop, Pane.scrollHeight, Pane.clientHeight) === false) { Pane.scrollTop = Pane.scrollHeight; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.ToBottom");
        }
    };

    /** The pill, made once per page (a button in `.thread`), shown now; a click or reaching the bottom hides it. */
    private ShowPill = (Doc: IChatDoc, Pane: IAppLivePane): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Pill === null) {
                let Home: IChatNode | null = Doc.querySelector(".thread");
                if (Home === null) { Home = Pane; }
                const Made: IAppLivePill = Doc.createElement("button") as IAppLivePill;
                Made.className = AppLiveRules.PillClass;
                Made.setAttribute("type", "button");
                Made.textContent = AppLiveRules.PillText;
                Made.addEventListener("click", this.OnPill);
                (Home as IChatNode).appendChild(Made);
                Pane.addEventListener("scroll", this.OnScroll);
                this.Pill = Made;
            }
            (this.Pill as IAppLivePill).removeAttribute("hidden");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.ShowPill");
        }
    };

    private OnPill = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc !== null) {
                const Pane: IAppLivePane | null = (Doc as IChatDoc).querySelector(".chat") as IAppLivePane | null;
                if (Pane !== null) { AppLiveFeed.ToBottom(Pane as IAppLivePane); }
            }
            this.HidePill();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.OnPill");
        }
    };

    private OnScroll = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: IChatDoc | null = this.Appender.Page();
            if (Doc === null || this.Pill === null) { return; }
            const Pane: IAppLivePane | null = (Doc as IChatDoc).querySelector(".chat") as IAppLivePane | null;
            if (Pane === null) { return; }
            if (AppLiveRules.AtBottom(Pane.scrollTop, Pane.scrollHeight, Pane.clientHeight) === true) { this.HidePill(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.OnScroll");
        }
    };

    private HidePill = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Pill !== null) { (this.Pill as IAppLivePill).setAttribute("hidden", ""); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.HidePill");
        }
    };

    /** The page's newest UTC minute, from its bubble ids (`m-YYYYMMDDHHMM`), or "". */
    public static NewestMinute = (Doc: IChatDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppChatRules.Newest(AppChatAppend.Values(Doc, "id", ".b"));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.NewestMinute");
            return "";
        }
    };

    /** The `data-mid`s of the page's bubbles in that minute - the first ask's `seen`. */
    public static MidsIn = (Doc: IChatDoc, Minute: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            const All: ArrayLike<IChatNode> = Doc.querySelectorAll(".b");
            for (let At: number = 0; At < All.length; At = At + 1) {
                const Mid: string | null = All[At].getAttribute(AppChatAppend.MidAttr);
                if (Mid === null || Mid.length === 0) { continue; }
                if (AppChatRules.MinuteOfId(nz(All[At].getAttribute("id"))) === Minute) { Out.push(Mid); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveFeed.MidsIn");
            return [];
        }
    };
}

/** The browser's transport: one XHR at a time, abortable. No client timeout - the service holds, then answers. */
export class AppLivePost implements IAppLivePost {
    private Req: XMLHttpRequest | null = null;
    private Done: ((Status: number, Text: string) => void) | null = null;

    public Post = (Url: string, Body: string, Done: (Status: number, Text: string) => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Done = Done;
            const Req: XMLHttpRequest = new XMLHttpRequest();
            this.Req = Req;
            Req.onreadystatechange = this.OnState;
            Req.open("POST", Url, true);
            Req.setRequestHeader("Content-Type", "application/json");
            Req.send(Body);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLivePost.Post");
            this.Finish(0, "");
        }
    };

    public Abort = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - dropping a request must work whatever the gate says.
        try {
            const Req: XMLHttpRequest | null = this.Req;
            this.Req = null;
            this.Done = null;
            if (Req !== null) { (Req as XMLHttpRequest).abort(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLivePost.Abort");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the feed is waiting on this answer. */
    private OnState = (): void => {
        try {
            const Req: XMLHttpRequest | null = this.Req;
            if (Req === null || (Req as XMLHttpRequest).readyState !== 4) { return; }
            this.Finish((Req as XMLHttpRequest).status, (Req as XMLHttpRequest).responseText);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLivePost.OnState");
        }
    };

    /** ⛔ The ONLY place `Done` is called, latched before delivering (an aborted request delivers nothing). */
    private Finish = (Status: number, Text: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the one thing that releases the feed.
        try {
            const Done: ((Status: number, Text: string) => void) | null = this.Done;
            this.Done = null;
            this.Req = null;
            if (Done !== null) { Done(Status, Text); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLivePost.Finish");
        }
    };
}

/** The browser's timers. */
export class AppLiveWindowClock implements IAppLiveClock {
    public Later = (Ms: number, Fn: () => void): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return window.setTimeout(Fn, Math.max(0, Ms));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveWindowClock.Later");
            return 0;
        }
    };

    public Cancel = (Id: number): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a cancel must work whatever the gate says.
        try {
            window.clearTimeout(Id);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveWindowClock.Cancel");
        }
    };
}

/** The pending-ask state both live views hold; AppLiveKick owns the guard, cut and re-arm around it. */
export class AppLiveLatch {
    public readonly Clock: IAppLiveClock;
    public readonly Ask: () => void;
    public Timer: number = 0;
    public constructor(Clock: IAppLiveClock, Ask: () => void) { this.Clock = Clock; this.Ask = Ask; }
}

/** The shared ask-now / ask-later latch ops of the list feed and the page feed. `Extra` is one more refuse-guard (the page feed's Hidden). */
export class AppLiveKick {
    /** Ask at once: cut a pending wait short, then ask. */
    public static Now = (Stopped: boolean, Extra: boolean, InFlight: boolean, Begun: boolean, Latch: AppLiveLatch, Label: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Stopped === true || Extra === true || InFlight === true || Begun === false) { return; }
            if (Latch.Timer !== 0) { Latch.Clock.Cancel(Latch.Timer); Latch.Timer = 0; }
            Latch.Ask();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
        }
    };

    /** Re-arm the ask timer after a wait; a refused re-arm leaves the latch untouched. */
    public static Again = (Stopped: boolean, Extra: boolean, Ms: number, Latch: AppLiveLatch, Label: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Stopped === true || Extra === true) { return; }
            Latch.Timer = Latch.Clock.Later(Ms, Latch.Ask);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
        }
    };
}